import React, { useMemo, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../Global/Layout';
import DataTable from '../../Global/DataTable';
import { showConfirm, showError, showSuccess } from '../../../utils/sweetAlertConfig';
import Select from 'react-select';
import useBodyScrollLock from '../../../hooks/useBodyScrollLock';
import { API_CONFIG, MSG_REMARK, ROLE_DISPLAY, ROLE_DISPLAY_TABLE, CUSTOMER_TIER_DISPLAY, CUSTOMER_TIER_DISPLAY_TABLE } from '../../../contexts/constants';

const Members = ({ onLogout, userRole }) => {
  const location = useLocation();
  const filter_agent_id = useMemo(() => {
    const raw = new URLSearchParams(location.search).get('cida');
    return raw ? String(raw) : null;
  }, [location.search]);
  const filter_agent_name = useMemo(() => {
    const raw = new URLSearchParams(location.search).get('cna');
    return raw ? String(raw) : null;
  }, [location.search]);
  const [filterAgent, setFilterAgent] = useState('');
  const [members, setMembers] = useState([]);
  const [totalMembers, setTotalMembers] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [currentMember, setCurrentMember] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
    phone: '',
    lineID: '',
    promptPay: '',
    promptPayName: '',
    minBet: 1,
    maxBet: 10000,
    bankName: '',
    bankOtherName: '',
    bankAccountName: '',
    bankAccountNumber: '',
    agentId: '',
	agentName: '',
    role: 'customer',
    tier: 'normal',
    isActive: true
  });
  const [passwordError, setPasswordError] = useState('');
  const [showSearchForm, setShowSearchForm] = useState(false);
  const roleOptions = ['sysadmin', 'owner', 'admin'];

  // Helper function to get initial search filters based on userRole
  const getInitialSearchFilters = (currentUserRole) => {
    const initialRoles = {
      owner: false,
      admin: false,
      agent: false,
      customer: false
    };
    if (currentUserRole === 'agent') {
      initialRoles.customer = true;
    }
    return {
      name: '',
      roles: initialRoles,
      statuses: {
        active: false,
        inactive: false
      },
      tiers: {
        normal: false,
        silver: false,
        gold: false
      }
    };
  };
  const [searchFilters, setSearchFilters] = useState(() => getInitialSearchFilters(userRole));
  const [appliedSearchFilters, setAppliedSearchFilters] = useState(() => getInitialSearchFilters(userRole));
  const [filteredMembers, setFilteredMembers] = useState([]);

  // Lock body scroll when modal is open
  useBodyScrollLock(showModal);

  const [bankOptions, setBankOptions] = useState([]);
  const [agentOptions, setAgentOptions] = useState([]);

  useEffect(() => {
    fetchBanks();
    fetchAgents();
  }, []);

  useEffect(() => {
    fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, filter_agent_id, appliedSearchFilters]);

  const fetchBanks = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_CONFIG.BASE_URL}/banks`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.success && Array.isArray(res.data.data)) {
        const options = res.data.data.map(b => ({ value: b.id, label: b.name }));
        if (!options.find(o => Number(o.value) === 99)) {
          options.push({ value: 99, label: 'อื่นๆ (ระบุ)' });
        }
        setBankOptions(options);
      }
    } catch (error) {
      console.error('Error fetching banks:', error);
    }
  };

  const fetchAgents = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_CONFIG.BASE_URL}/members/agents`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.success && Array.isArray(res.data.data)) {
        const options = res.data.data.map(a => ({
          value: a.id,
          label: a.fullName || a.username
        }));
        setAgentOptions(options);
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  // Filter members based on search criteria - now handled by backend
  useEffect(() => {
    // Filter by agent from query (?cida=agentId)
	setFilterAgent(filter_agent_name || '');

    setFilteredMembers(members);
  }, [members, filter_agent_name]);



	if (!['sysadmin', 'owner', 'admin', 'agent'].includes(userRole)) {
		window.location.href = '/forbidden';
		return null;
	}

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: pageSize
      };

      // เพิ่ม agentId ถ้ามีการกรองตาม agent
      if (filter_agent_id) {
        params.agentId = filter_agent_id;
      }

      // เพิ่ม search parameters
      if (appliedSearchFilters.name) {
        params.name = appliedSearchFilters.name;
      }

      const selectedRoles = Object.keys(appliedSearchFilters.roles).filter(role => appliedSearchFilters.roles[role]);
      if (selectedRoles.length > 0) {
        params.roles = selectedRoles;
      }

      const selectedStatuses = Object.keys(appliedSearchFilters.statuses).filter(status => appliedSearchFilters.statuses[status]);
      if (selectedStatuses.length > 0) {
        params.statuses = selectedStatuses;
      }

      const selectedTiers = Object.keys(appliedSearchFilters.tiers).filter(tier => appliedSearchFilters.tiers[tier]);
      if (selectedTiers.length > 0) {
        params.tiers = selectedTiers;
      }

      const response = await axios.get(`${API_CONFIG.BASE_URL}/members`, {
        params,
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.data.success && Array.isArray(response.data.data)) {
        setMembers(response.data.data);
        setTotalMembers(response.data.total || 0);
        setTotalPages(response.data.totalPages || 1);
      } else {
        console.error('Invalid members data format');
        setMembers([]);
        setTotalMembers(0);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Error fetching members:', error);

      // แสดง error message จาก API
      let errorMessage = 'ไม่สามารถโหลดข้อมูลสมาชิกได้';

      if (error.response) {
        // Server ตอบกลับมาด้วย status code ที่ไม่ใช่ 2xx
        errorMessage = error.response.data?.message || `เกิดข้อผิดพลาด (${error.response.status})`;

        // ถ้าเป็น 401 หรือ 403 ให้ logout
        if (error.response.status === 401 || error.response.status === 403) {
          errorMessage = error.response.data?.message || 'กรุณาเข้าสู่ระบบใหม่';
          showError('เซสชันหมดอายุ', errorMessage);
          setTimeout(() => {
            localStorage.removeItem('token');
            onLogout();
          }, 1500);
          return;
        }
      } else if (error.request) {
        // Request ถูกส่งไปแต่ไม่ได้รับ response
        errorMessage = 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้';
      } else {
        // เกิดข้อผิดพลาดอื่นๆ
        errorMessage = error.message || 'เกิดข้อผิดพลาดที่ไม่ทราบสาเหตุ';
      }

      showError('เกิดข้อผิดพลาด', errorMessage);
      setMembers([]);
      setTotalMembers(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setModalMode('add');
    setFormData({
      username: '',
      firstName: '',
      lastName: '',
      password: '',
      confirmPassword: '',
      phone: '',
      lineID: '',
      minBet: 1,
      bankName: '',
      bankAccountName: '',
      bankAccountNumber: '',
      maxBet: 10000,
      agentId: '',
	  agentName: '',
      role: 'customer',
      tier: 'normal',
      isActive: true
    });
    setPasswordError('');
    setCurrentMember(null);
    setShowModal(true);
  };

  const handleEdit = (member) => {
    setModalMode('edit');
    setFormData({
      username: member.username,
      firstName: member.firstName,
      lastName: member.lastName || '',
      password: '',
      confirmPassword: '',
      phone: member.phone || '',
      lineID: member.lineID || '',
      minBet: member.minBet,
      maxBet: member.maxBet,
	  bankAccountName: member.bankAccountName || '',
	  bankAccountNumber: member.bankAccountNumber || '',
	  promptPay: member.promptPay || '',
	  promptPayName: member.promptPayName || '',
      agentId: member.agentId ? String(member.agentId) : '',
	  agentName: member.agent || '',
      role: member.role || 'customer',
      tier: member.tier || 'normal',
      isActive: member.isActive
    });
    // map bankName (string) to bank id if possible
    const bankMatch = bankOptions.find(b => b.label === (member.bankName || ''));
    if (bankMatch) {
      setFormData(prev => ({ ...prev, bankName: bankMatch.value }));
    } else if (member.bankName) {
      setFormData(prev => ({ ...prev, bankName: 99, bankOtherName: member.bankName }));
    } else {
      setFormData(prev => ({ ...prev, bankName: '' }));
    }
    setPasswordError('');
    setCurrentMember(member);
    setShowModal(true);
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setFormData({ ...formData, password: newPassword });

    // Check password match on change
    if (formData.confirmPassword && newPassword !== formData.confirmPassword) {
      setPasswordError('รหัสผ่านไม่ตรงกัน');
    } else {
      setPasswordError('');
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const confirmPass = e.target.value;
    setFormData({ ...formData, confirmPassword: confirmPass });

    // Check password match on change
    if (formData.password && confirmPass !== formData.password) {
      setPasswordError('รหัสผ่านไม่ตรงกัน');
    } else {
      setPasswordError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate password match
    if (modalMode === 'add' || formData.password) {
      if (formData.password !== formData.confirmPassword) {
        setPasswordError('รหัสผ่านและยืนยันรหัสผ่านไม่ตรงกัน');
        return;
      }
      if (formData.password.length < 6) {
        showError('ข้อมูลไม่ถูกต้อง', 'รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร');
        return;
      }
    }

    // Validate username for add mode
    if (modalMode === 'add' && !formData.username.trim()) {
      showError('ข้อมูลไม่ถูกต้อง', 'กรุณากรอกชื่อผู้ใช้');
      return;
    }

    // Validate password for add mode
    if (modalMode === 'add' && !formData.password.trim()) {
      showError('ข้อมูลไม่ถูกต้อง', 'กรุณากรอกรหัสผ่าน');
      return;
    }

    // Validate min/max bet
    if (parseFloat(formData.minBet) > parseFloat(formData.maxBet)) {
      showError('ข้อมูลไม่ถูกต้อง', 'สั่งซื้อขั้นต่ำต้องไม่มากกว่าสั่งซื้อสูงสุด');
      return;
    }

	// Validate Line ID - must start with @
	if (formData.lineID && !formData.lineID.startsWith('@')) {
		showError('ไอดีไลน์ต้องขึ้นต้นด้วย @');
		return;
	}

	// Validate Line ID - must start with @
	if (formData.lineID && !formData.lineID.startsWith('@')) {
		showError('ไอดีไลน์ต้องขึ้นต้นด้วย @');
		return;
	}

    const confirmTitle = modalMode === 'add' ? 'เพิ่มสมาชิกใหม่?' : 'บันทึกการแก้ไข?';
    const confirmText = modalMode === 'add'
      ? 'คุณต้องการเพิ่มสมาชิกใหม่ใช่หรือไม่?'
      : 'คุณต้องการบันทึกการแก้ไขใช่หรือไม่?';

    const result = await showConfirm({
      title: confirmTitle,
      text: confirmText,
      confirmText: 'ตกลง',
      cancelText: 'ยกเลิก',
      confirmVariant: modalMode === 'add' ? 'success' : 'primary',
      reverseButtons: true
    });

    if (!result.isConfirmed) return;

    try {
      setLoading(true);

      const requestData = {
        username: formData.username,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        lineID: formData.lineID,
        promptPay: (formData.promptPay || null) == '' ? null : formData.promptPay,
        promptPayName: (formData.promptPayName || null) == '' ? null : formData.promptPayName,
        minBet: parseFloat(formData.minBet),
        maxBet: parseFloat(formData.maxBet),
        bankId: formData.bankName ? (Number(formData.bankName) === 99 ? null : Number(formData.bankName)) : null,
        bankName: Number(formData.bankName) === 99 ? (formData.bankOtherName || null) : (bankOptions.find(b => b.value === formData.bankName)?.label || null),
        bankAccountName: (formData.bankAccountName || null) == '' ? null : formData.bankAccountName,
        bankAccountNumber: (formData.bankAccountNumber || null) == '' ? null : formData.bankAccountNumber,
        agentId: formData.role === 'customer' && formData.agentId ? Number(formData.agentId) : null,
        role: formData.role,
        tier: formData.role === 'customer' ? formData.tier : null
      };

      // Add password only if provided
      if (formData.password && formData.password.trim() !== '') {
        requestData.password = formData.password;
      }

      let response;
      if (modalMode === 'add') {
        response = await axios.post(
          `${API_CONFIG.BASE_URL}/members`,
          requestData,
          { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
        );
      } else {
        response = await axios.put(
          `${API_CONFIG.BASE_URL}/members/${currentMember.id}`,
          requestData,
          { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
        );
      }

      if (response.data.success) {
        // Refresh members list
        await fetchMembers();

        setShowModal(false);
        setFormData({
          username: '',
          firstName: '',
          lastName: '',
          password: '',
          confirmPassword: '',
          phone: '',
          lineID: '',
          promptPay: '',
          promptPayName: '',
          minBet: 1,
          maxBet: 10000,
          bankName: '',
          bankOtherName: '',
          bankAccountName: '',
          bankAccountNumber: '',
          agentId: '',
          role: 'customer',
          tier: 'normal',
          isActive: true
        });
        setPasswordError('');
        setCurrentMember(null);

        showSuccess('สำเร็จ!', response.data.message || (modalMode === 'add' ? 'เพิ่มสมาชิกเรียบร้อยแล้ว' : 'บันทึกข้อมูลเรียบร้อยแล้ว'));
      } else {
        showError('เกิดข้อผิดพลาด', response.data.message || 'ไม่สามารถบันทึกข้อมูลได้');
      }
    } catch (error) {
      console.error('Error saving member:', error);
      let errorMessage = 'ไม่สามารถบันทึกข้อมูลได้';

      if (error.response) {
        errorMessage = error.response.data?.message || `เกิดข้อผิดพลาด (${error.response.status})`;

        if (error.response.status === 401 || error.response.status === 403) {
          errorMessage = error.response.data?.message || 'กรุณาเข้าสู่ระบบใหม่';
          showError('เซสชันหมดอายุ', errorMessage);
          setTimeout(() => {
            localStorage.removeItem('token');
            onLogout();
          }, 1500);
          return;
        }
      } else if (error.request) {
        errorMessage = 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้';
      }

      showError('เกิดข้อผิดพลาด', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const response = await axios.put(
        `${API_CONFIG.BASE_URL}/members/${id}/toggle-status`,
        {},
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );

      if (response.data.success) {
        // Update local state
        setMembers(members.map(m =>
          m.id === id ? { ...m, isActive: response.data.isActive } : m
        ));
        //showSuccess('สำเร็จ!', response.data.message);
      } else {
        showError('เกิดข้อผิดพลาด', response.data.message || 'ไม่สามารถเปลี่ยนสถานะได้');
      }
    } catch (error) {
      console.error('Error toggling member status:', error);
      let errorMessage = 'ไม่สามารถเปลี่ยนสถานะได้';

      if (error.response) {
        errorMessage = error.response.data?.message || `เกิดข้อผิดพลาด (${error.response.status})`;

        if (error.response.status === 401 || error.response.status === 403) {
          errorMessage = error.response.data?.message || 'กรุณาเข้าสู่ระบบใหม่';
          showError('เซสชันหมดอายุ', errorMessage);
          setTimeout(() => {
            localStorage.removeItem('token');
            onLogout();
          }, 1500);
          return;
        }
      } else if (error.request) {
        errorMessage = 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้';
      }

      showError('เกิดข้อผิดพลาด', errorMessage);
    }
  };

  const columns = [
    {
      key: 'actions',
      header: 'จัดการ',
      className: 'text-center',
	  width: 80,
      render: (row) => (
        <div className="flex gap-2 justify-center">
          <button
            onClick={() => handleEdit(row)}
            className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition"
            title="แก้ไข"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
        </div>
      )
    },
    {
		key: 'no',
		header: 'ลำดับ',
		className: 'text-center',
		width: 100,
		render: (row) => (
			<div className="flex gap-2 justify-center">{row.no}</div>
		)
	},
    { key: 'username', header: 'ชื่อผู้ใช้' },
    {
      	key: 'fullName',
      	header: 'ชื่อ-นามสกุล',
		render: (row) => {
			return row.role === 'agent' ? (
				<span
					onClick={() => {
						window.history.replaceState(null, '', `?cna=${row.firstName} ${row.lastName || ''}&cida=${row.id}`);
						window.location.reload(true);
					}}
					className="inline-flex items-center text-blue-500 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors max-w-[150px] sm:max-w-none cursor-pointer truncate"
				>
					{row.firstName} {row.lastName || ''}
				</span>
			) : `${row.firstName} ${row.lastName || ''}`
		}
    },
    {
      key: 'role',
      header: 'กลุ่มสิทธิ์',
	  className: 'text-center',
	//   width: 120,
      render: (row) => {
        return (
          <span className={`px-3 py-1 rounded-full text-xs font-medium flex gap-2 justify-center ${ROLE_DISPLAY_TABLE[row.role] || ROLE_DISPLAY_TABLE.customer}`}>
            {ROLE_DISPLAY[row.role] || 'ลูกค้า'}
          </span>
        );
      }
    },
    {
      key: 'tier',
      header: 'ระดับลูกค้า',
      className: 'text-center',
      render: (row) => {
        if (row.role !== 'customer') return <span className="text-gray-400 text-xs">-</span>;
        return (
          <span className={`px-3 py-1 rounded-full text-xs font-medium flex gap-2 justify-center ${CUSTOMER_TIER_DISPLAY_TABLE[row.tier] || CUSTOMER_TIER_DISPLAY_TABLE.normal}`}>
            {CUSTOMER_TIER_DISPLAY[row.tier] || 'Normal'}
          </span>
        );
      }
    },
    {
      key: 'isActive',
      header: 'สถานะ',
	  className: 'text-center',
	//   width: 150,
      render: (row) =>
		{return row.role !== 'sysadmin' && row.role !== 'owner' ?
        (<div className="flex justify-center">
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={row.isActive}
              onChange={() => handleToggleStatus(row.id)}
              className="sr-only peer"
            />
            <div className={`w-11 h-6 ${row.isActive ? 'bg-gray-300 dark:bg-gray-600' : 'bg-red-300 dark:bg-red-800'} peer-focus:outline-none peer-focus:ring-2 ${row.isActive ? 'peer-focus:ring-green-500' : 'peer-focus:ring-red-500'} rounded-full peer ${row.isActive ? 'peer-checked:bg-green-600' : 'peer-checked:bg-red-600'} peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all`}></div>
            <span className={`ml-2 text-sm ${row.isActive ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'} font-medium`}>
              {row.isActive ? 'ใช้งาน' : 'ระงับ'}
            </span>
          </label>
        </div>)
		: (<div className="flex justify-center"><span className={`ml-2 text-sm ${row.isActive ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'} font-medium`}>
			{row.isActive ? 'ใช้งาน' : 'ระงับ'}
		  </span></div>)
		}
    }
  ];

  const toggleSearchForm = () => {
    setShowSearchForm(!showSearchForm);
  };

  const handleSearch = () => {
    setAppliedSearchFilters({ ...searchFilters }); // Apply current search filters
    setCurrentPage(1); // Reset to first page when searching
  };

  const displayMembers = filteredMembers;
  const safeCurrentPage = Math.min(currentPage, totalPages);

  const gotoPage = (p) => {
    if (p < 1 || p > totalPages) return;
    setCurrentPage(p);
  };

  // ไม่ต้อง slice เพราะ backend ส่งมาแล้ว
  const paginatedMembers = displayMembers;

  const handleClearSearch = () => {
    const initialFilters = getInitialSearchFilters(userRole);
    setSearchFilters(initialFilters);
    setAppliedSearchFilters(initialFilters);
    setCurrentPage(1);
  };

  return (
    <Layout title={`รายการข้อมูล${roleOptions.includes(userRole)?'สมาชิก' : 'ลูกค้าในเครือ'}`} onLogout={onLogout} userRole={userRole}>
      {/* Header with Add button */}
      <div className="mb-4 flex justify-between items-center">
        <div>
          <p className="text-gray-600 dark:text-gray-400">
            จัดการข้อมูล{roleOptions.includes(userRole)?'สมาชิก' : 'ลูกค้าในเครือ'}ทั้งหมด
          </p>
        </div>
        <div className="flex items-center gap-3">
		  	<button
				onClick={toggleSearchForm}
				className={`flex items-center gap-2 px-4 py-2 text-white rounded-lg transition shadow-lg ${
					showSearchForm
					? 'bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700'
					: 'bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700'
				}`}
				>
				{showSearchForm ? (
					<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
					</svg>
				) : (
					<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
					</svg>
				)}
				<span className='hidden md:inline'>ค้นหา</span>
			</button>
          <button
            onClick={handleAdd}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800 text-white rounded-lg transition shadow-lg"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className='hidden md:inline'>เพิ่มสมาชิก</span>
          </button>
        </div>
      </div>

      {/* Search Filter Form - Show/Hide */}
      {showSearchForm && (
        <div className="mb-6 bg-white dark:bg-surface-dark border border-borderc-light dark:border-borderc-dark rounded-xl shadow-card dark:shadow-carddark p-4 animate-fade-in">
          <h3 className="text-lg font-semibold text-black dark:text-white mb-4">ค้นหาข้อมูล</h3>
          <div className="space-y-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                ชื่อ-นามสกุล
              </label>
              <input
                type="text"
                value={searchFilters.name}
                onChange={(e) => setSearchFilters({ ...searchFilters, name: e.target.value })}
                placeholder="ค้นหาชื่อ หรือนามสกุล"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {roleOptions.includes(userRole) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    กลุ่มสิทธิ์
                  </label>
                  <div className="space-y-2 flex items-end">
                    <label className="flex items-center space-x-2 cursor-pointer pe-3">
                      <input
                        type="checkbox"
                        checked={searchFilters.roles.owner}
                        onChange={(e) => setSearchFilters({
                        ...searchFilters,
                        roles: { ...searchFilters.roles, owner: e.target.checked }
                        })}
                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">เจ้าของ</span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer pe-3">
                      <input
                        type="checkbox"
                        checked={searchFilters.roles.admin}
                        onChange={(e) => setSearchFilters({
                        ...searchFilters,
                        roles: { ...searchFilters.roles, admin: e.target.checked }
                        })}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">ผู้ดูแล</span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer pe-3">
                      <input
                        type="checkbox"
                        checked={searchFilters.roles.agent}
                        onChange={(e) => setSearchFilters({
                        ...searchFilters,
                        roles: { ...searchFilters.roles, agent: e.target.checked }
                        })}
                        className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">ตัวแทน</span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={searchFilters.roles.customer}
                        onChange={(e) => setSearchFilters({
                        ...searchFilters,
                        roles: { ...searchFilters.roles, customer: e.target.checked }
                        })}
                        className="w-4 h-4 text-gray-600 border-gray-300 rounded focus:ring-gray-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">ลูกค้า</span>
                    </label>
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  สถานะ
                </label>
                <div className="space-y-2 flex items-end">
                  <label className="flex items-center space-x-2 cursor-pointer pe-3">
                    <input
                      type="checkbox"
                      checked={searchFilters.statuses.active}
                      onChange={(e) => setSearchFilters({
                      ...searchFilters,
                      statuses: { ...searchFilters.statuses, active: e.target.checked }
                      })}
                      className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">ใช้งาน</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={searchFilters.statuses.inactive}
                      onChange={(e) => setSearchFilters({
                      ...searchFilters,
                      statuses: { ...searchFilters.statuses, inactive: e.target.checked }
                      })}
                      className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">ระงับ</span>
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  ระดับลูกค้า
                </label>
                <div className="space-y-2 flex items-end">
                  <label className="flex items-center space-x-2 cursor-pointer pe-3">
                    <input
                      type="checkbox"
                      checked={searchFilters.tiers.normal}
                      onChange={(e) => setSearchFilters({
                      ...searchFilters,
                      tiers: { ...searchFilters.tiers, normal: e.target.checked }
                      })}
                      className="w-4 h-4 text-gray-600 border-gray-300 rounded focus:ring-gray-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Normal</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer pe-3">
                    <input
                      type="checkbox"
                      checked={searchFilters.tiers.silver}
                      onChange={(e) => setSearchFilters({
                      ...searchFilters,
                      tiers: { ...searchFilters.tiers, silver: e.target.checked }
                      })}
                      className="w-4 h-4 text-slate-600 border-gray-300 rounded focus:ring-slate-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Silver</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={searchFilters.tiers.gold}
                      onChange={(e) => setSearchFilters({
                      ...searchFilters,
                      tiers: { ...searchFilters.tiers, gold: e.target.checked }
                      })}
                      className="w-4 h-4 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Gold</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md font-medium transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              ค้นหา
            </button>
            <button
              onClick={handleClearSearch}
              className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-md font-medium transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428l-7.071 7.071L9 19.142 2.929 13.07l7.071-7.071L12.357 8.357l7.071 7.071z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2l1 3h8l-1 1-8 8-1-1 8-8 1-1z" />
              </svg>
              ล้าง
            </button>
          </div>
        </div>
      )}

	  {filterAgent && (
		<div className="flex justify-between items-center mb-4 p-4 bg-blue-100 dark:bg-blue-800 border border-blue-200 dark:border-blue-700 rounded-lg">
			<p className="text-blue-800 dark:text-blue-200">
				ลูกค้าของ: <span className="font-semibold">{filterAgent}</span>
			</p>
			<button
				onClick={() => {
					setFilterAgent('');
					window.history.replaceState(null, '', window.location.pathname);
					window.location.reload(true);
				}}
				className="px-3 py-1 bg-yellow-500 hover:bg-yellow-600 text-white rounded-md text-sm transition"
			>
				ล้างตัวกรอง
			</button>
		</div>
	  )}

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={paginatedMembers}
        loading={loading}
        page={safeCurrentPage}
        pageSize={pageSize}
        totalItems={totalMembers}
        onPageChange={gotoPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
        emptyMessage="ไม่มีข้อมูลสมาชิก"
      />

      {/* Modal Form */}
      {showModal && (
        <div className="fixed top-0 inset-x-0 bg-black/50 flex items-top justify-center z-50 p-4 max-h-screen h-full overflow-y-auto flex align-middle">
          <div className="flex align-middle h-auto my-auto">
            <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl h-auto w-full max-w-full sm:max-w-lg min-w-full sm:min-w-[800px] my-5">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                {modalMode === 'add' ? 'เพิ่มสมาชิกใหม่' : 'แก้ไขข้อมูลสมาชิก'}
              </h3>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                      ชื่อ <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      required
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-blue-500"
                      placeholder="กรอกชื่อ"
                    />
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                      นามสกุล
                    </label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-blue-500"
                      placeholder="กรอกนามสกุล"
                    />
                  </div>
                </div>

                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  ชื่อผู้ใช้ <span className="text-red-500">*</span>
                  </label>
                  <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-blue-500"
                  placeholder="กรอกชื่อผู้ใช้"
                  />
                </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  รหัสผ่าน {modalMode === 'add' && <span className="text-red-500">*</span>}
                  {modalMode === 'edit' && <span className="text-gray-500 text-xs">(เว้นว่างหากไม่ต้องการเปลี่ยน)</span>}
                  </label>
                  <input
                  type="password"
                  value={formData.password}
                  onChange={handlePasswordChange}
                  required={modalMode === 'add'}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-blue-500"
                  placeholder="กรอกรหัสผ่าน (อย่างน้อย 6 ตัวอักษร)"
                  minLength={6}
                  />
                </div>
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  ยืนยันรหัสผ่าน {modalMode === 'add' && <span className="text-red-500">*</span>}
                  </label>
                  <input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  required={modalMode === 'add' || formData.password}
                  className={`w-full px-4 py-2 rounded-lg border ${passwordError ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-blue-500`}
                  placeholder="กรอกรหัสผ่านอีกครั้ง"
                  />
                  {passwordError && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{passwordError}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    เบอร์โทร
                    </label>
                    <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-blue-500"
                    placeholder="เบอร์โทรศัพท์"
                    />
                </div>

                <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    ไอดีไลน์
                    </label>
                    <input
                    type="text"
                    value={formData.lineID}
                    onChange={(e) => setFormData({ ...formData, lineID: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-blue-500"
                    placeholder="ไอดีไลน์"
                    />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Min Bet <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={formData.minBet}
                  onChange={(e) => setFormData({ ...formData, minBet: e.target.value })}
                  required
                  min="1"
                  step="1"
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-blue-500"
                  placeholder="1"
                />
                </div>

                <div>
                <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Max Bet <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={formData.maxBet}
                  onChange={(e) => setFormData({ ...formData, maxBet: e.target.value })}
                  required
                  min="1"
                  step="1"
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-blue-500"
                  placeholder="10000"
                />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    ธนาคาร
                  </label>
                  <Select
                    options={bankOptions}
                    value={bankOptions.find(opt => opt.value === (formData.bankName || '')) || null}
                    onChange={opt => setFormData({ ...formData, bankName: opt ? opt.value : '' })}
                    isClearable
                    placeholder="-- เลือกธนาคาร --"
                    classNamePrefix="react-select"
                    className="react-select-container"
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: '43.2px',
                        borderColor: '#d1d5db',
                        borderRadius: '0.5rem',
                        backgroundColor: 'white',
                      }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 9999
                      })
                    }}
                  />
                  {MSG_REMARK.BANK_PAY}
                </div>
                <div>
                  {Number(formData.bankName) === 99 && (
                    <>
                      <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                        ระบุชื่อธนาคาร
                      </label>
                      <input
                        type="text"
                        value={formData.bankOtherName || ''}
                        onChange={e => setFormData({ ...formData, bankOtherName: e.target.value })}
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-blue-500"
                        placeholder="กรอกชื่อธนาคาร"
                      />
                    </>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    ชื่อบัญชี
                  </label>
                  <input
                    type="text"
                    value={formData.bankAccountName || ''}
                    onChange={e => setFormData({ ...formData, bankAccountName: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-blue-500"
                    placeholder="ชื่อบัญชี"
                  />
                </div>
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    เลขที่บัญชี
                  </label>
                  <input
                    type="text"
                    value={formData.bankAccountNumber || ''}
                    onChange={e => setFormData({ ...formData, bankAccountNumber: e.target.value })}
					onInput={(e) => e.target.value = e.target.value.replace(/[^0-9]/g, '')}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-blue-500"
                    placeholder="เลขที่บัญชี"
                  />
                </div>
              </div>
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
					<div>
						<label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
						ชื่อบัญชีพร้อมเพย์
						</label>
						<input
						type="text"
						value={formData.promptPayName}
						onChange={(e) => setFormData({ ...formData, promptPayName: e.target.value })}
						className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-blue-500"
						placeholder="ชื่อบัญชีพร้อมเพย์"
						/>
					</div>

					<div>
						<label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
						เลขพร้อมเพย์
						</label>
						<input
						type="text"
						value={formData.promptPay}
						onChange={(e) => setFormData({ ...formData, promptPay: e.target.value })}
						onInput={(e) => e.target.value = e.target.value.replace(/[^0-9]/g, '')}
						className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-blue-500"
						placeholder="เลขบัตรประชาชน / เบอร์โทรศัพท์"
						/>
					</div>
			  	</div>

              {formData.role === 'customer' && (
                <>
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                      ตัวแทน{userRole === 'agent' ? ' '+(formData.agentName || '-') : ''}
                    </label>
					{formData.role === 'customer' && (userRole === 'sysadmin' || userRole ==='owner' || userRole === 'admin') ? (
					<Select
						options={agentOptions}
						value={agentOptions.find(opt => opt.value === Number(formData.agentId)) || null}
						onChange={opt => setFormData({ ...formData, agentId: opt ? String(opt.value) : '' })}
						isClearable
						placeholder="-- เลือกตัวแทน --"
						classNamePrefix="react-select"
						className="react-select-container"
						styles={{
							control: (base) => ({
								...base,
								minHeight: '43.2px',
								borderColor: '#d1d5db',
								borderRadius: '0.5rem',
								backgroundColor: 'white',
							}),
							menu: (base) => ({
								...base,
								zIndex: 9999
							})
						}}
					/>
					) : (
					<></>
					)}
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                      ระดับลูกค้า <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-4">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="tier"
                          value="normal"
                          checked={formData.tier === 'normal'}
                          onChange={(e) => setFormData({ ...formData, tier: e.target.value })}
                          className="w-4 h-4 text-gray-600 border-gray-300 focus:ring-gray-500"
                        />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Normal</span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="tier"
                          value="silver"
                          checked={formData.tier === 'silver'}
                          onChange={(e) => setFormData({ ...formData, tier: e.target.value })}
                          className="w-4 h-4 text-slate-600 border-gray-300 focus:ring-slate-500"
                        />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Silver</span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="tier"
                          value="gold"
                          checked={formData.tier === 'gold'}
                          onChange={(e) => setFormData({ ...formData, tier: e.target.value })}
                          className="w-4 h-4 text-yellow-600 border-gray-300 focus:ring-yellow-500"
                        />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Gold</span>
                      </label>
                    </div>
                  </div>
                </>
              )}


              <div className={`grid grid-cols-1 ${roleOptions.includes(userRole) ? 'sm:grid-cols-1' : 'sm:grid-cols-2'} gap-3`}>
				{formData.role !== 'owner' && (
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    กลุ่มสิทธิ์ <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-4">
                    {roleOptions.includes(userRole) && (
                      <>
              {/* {userRole === 'owner' || userRole === 'sysadmin' ?
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                  type="radio"
                  name="role"
                  value="owner"
                  checked={formData.role === 'owner'}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">เจ้าของ</span>
                </label>
              : <></>} */}
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                type="radio"
                name="role"
                value="admin"
                checked={formData.role === 'admin'}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">ผู้ดูแล</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                type="radio"
                name="role"
                value="agent"
                checked={formData.role === 'agent'}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">ตัวแทน</span>
              </label>
                      </>
                    )}
                    <label className="flex items-center space-x-2 cursor-pointer">
				<input
				type="radio"
				name="role"
				value="customer"
				checked={formData.role === 'customer'}
				onChange={(e) => setFormData({ ...formData, role: e.target.value })}
				className="w-4 h-4 text-gray-600 border-gray-300 focus:ring-gray-500"
				/>
				<span className="text-sm font-medium text-gray-700 dark:text-gray-300">ลูกค้า</span>
                    </label>
                  </div>
                </div>
				)}
				{userRole !== 'sysadmin' ?
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    สถานะ <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      เปิดใช้งานบัญชี
                    </span>
                    </label>
                    </div>
                </div>
				: <></>}
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-6 py-2 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-white rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition"
                >
                ยกเลิก
                </button>
                <button
                type="submit"
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition"
                >
                บันทึก
                </button>
              </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Members;
